import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import s from "../modules/Primary.module.css";
import { useT, useI18nReady } from "./TextProvider";

export default function Primary() {

  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const observerRef = useRef(null);

  // Вспомогательные
  const stripHTML = (html = "") => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncate = (text = "", max = 120) =>
    text.length > max ? text.slice(0, max).trim() + "…" : text;

  // Подгрузка карточек
  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      try {
        setLoading(true);
        setError(null);
        
        console.log("Starting fetch from fanlar API...");
        
        // Собираем заголовки, включая авторизацию если есть
        const headers = {
          "Content-Type": "application/json",
          "Accept": "application/json",
        };
        
        try {
          let token = null;
          token = localStorage.getItem("auth:token") ||
                  localStorage.getItem("auth:access") ||
                  localStorage.getItem("auth:key") ||
                  localStorage.getItem("auth:jwt") ||
                  localStorage.getItem("auth:auth_token");

          if (token) {
            const isJwt = token.startsWith("eyJ");
            headers["Authorization"] = isJwt ? `Bearer ${token}` : `Token ${token}`;
          }
        } catch (e) {
          console.warn("Error getting auth token:", e);
        }
        
        // Пробуем основной эндпоинт
        let res = await fetch("https://apitr.subxonqulov.uz/api/fanlar/", {
          signal: controller.signal,
          headers,
        });
        
        console.log("Response status:", res.status, res.statusText);
        
        // Если 500, пробуем без авторизации
        if (res.status === 500) {
          console.warn("Got 500 error, trying without auth headers...");
          res = await fetch("https://apitr.subxonqulov.uz/api/fanlar/", {
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
            }
          });
          console.log("Retry status:", res.status);
        }
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error("Error response body:", errorText);
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        
        const data = await res.json();
        console.log("Loaded tasks:", data);
        setTasks(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Error loading tasks:", e);
          setError("Не удалось загрузить карточки. Попробуйте обновить страницу.");
        }
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => controller.abort();
  }, []);

  // Плавное появление блоков при скролле (в том числе после загрузки)
  useEffect(() => {
    const els = document.querySelectorAll(".js-fade");
    if (!("IntersectionObserver" in window)) {
      // Фоллбек: просто показать
      els.forEach((el) => el.classList.add(s.visible));
      return;
    }
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            target.classList.add(s.visible);
            observerRef.current.unobserve(target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
    );
    els.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, [tasks]);

  return (
    <div className={s.container}>
      <div className={s.hero}>
        <span>{t('AI')}</span> {t('AI TRENER')}
      </div>
      <p className={`${s.heroSub} js-fade ${s.fadeIn}`}>
        {t('Научись писать')}
      </p>

      {/* Cards */}
      <section className={s.cards}>
        {loading && (
          <>
            <div className={`${s.card} ${s.skeleton}`} />
            <div className={`${s.card} ${s.skeleton}`} />
            <div className={`${s.card} ${s.skeleton}`} />
          </>
        )}

        {error && (
          <div className={s.error}>
            {error} Попробуйте обновить страницу.
          </div>
        )}

        {!loading && !error && tasks.length === 0 && (
          <div className={s.empty}>Пока нет заданий.</div>
        )}

        
        {!loading && !error && tasks.map((task) => {
          const title = task.name || "Без названия";
          const img = task.img || "/images/logo192.png";
          const descSource = task.description || stripHTML(task.savol) || "Описание отсутствует";
          const desc = truncate(descSource, 140);

          return (
            <Link
              key={task.id}
              to={`/mappage/${task.id}`}
              className={`${s.card} js-fade ${s.fadeIn}`}
            >
              <img
                src={img}
                alt={title}
                className={s.cardImg}
                loading="lazy"
                onError={(e) => { e.currentTarget.src = "/images/logo192.png"; }}
              />
              <p className={s.cardTitle}>{title}</p>
              <p className={s.cardDesc}>{desc}</p>
              <div className={s.cardActions}>
                <button
                  className="btn btn-outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate("/statistics");
                  }}
                >
                  {t('Статистика')}
                </button>
              </div>
            </Link>
          );
        })}

      </section>

      {/* Info */}
      <section className={s.info}>
        <h2 className={`${s.sectionTitle} js-fade ${s.fadeIn}`}>
          {t('Почему важно уметь')}
        </h2>
        <p className={`${s.sectionSub} js-fade ${s.fadeIn}`}>
          {t('ИИ понимает тебя')}
        </p>

        <div className={s.infoBlock}>
          <div className={s.comparison}>
            <div className={`${s.infoCard} ${s.bad} js-fade ${s.fadeIn}`}>
              <div className={s.infoContent}>
                <h3>{t('Плохой запрос')}</h3>
                <p>{t('«Напиши эссе»')}</p>
              </div>
              <div className={s.result}>
                {t('Короткий и общий текст')}
              </div>
              <svg viewBox="0 0 365.717 365" className={s.bgIcon} aria-hidden="true">
                <g fill="#f44336">
                  <path d="M356.34 296.348 69.727 9.734c-12.5-12.5-32.766-12.5-45.247 0L9.375 24.816c-12.5 12.504-12.5 32.77 0 45.25L295.988 356.68c12.504 12.5 32.77 12.5 45.25 0l15.082-15.082c12.524-12.48 12.524-32.75.02-45.25z"/>
                  <path d="M295.988 9.734 9.375 296.348c-12.5 12.5-12.5 32.77 0 45.25l15.082 15.082c12.504 12.5 32.77 12.5 45.25 0L356.34 70.086c12.504-12.5 12.504-32.766 0-45.246L341.258 9.758c-12.5-12.524-32.766-12.524-45.27-.024z"/>
                </g>
              </svg>
            </div>

            <div className={`${s.infoCard} ${s.good} js-fade ${s.fadeIn}`}>
              <div className={s.infoContent}>
                <h3>{t('Хороший запрос')}</h3>
                <p>
                  {t('«Напиши эссе на тему')}
                </p>
              </div>
              <div className={s.result}>
                {t('Структурированное эссе')}
              </div>
              <svg viewBox="0 0 512 512"  className={s.bgIcon} aria-hidden="true">
                <defs>
                  <linearGradient id="checkGradient" x1="394.91" x2="181.88" y1="448.88" y2="235.85" gradientTransform="matrix(0 -1 -1 0 598.36 478.25)" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#62e8a2" />
                    <stop offset="1" stopColor="#13ce63" />
                  </linearGradient>
                </defs>
                <path
                  fill="url(#checkGradient)"
                  d="M512 123.2c0 15.1-6 29.7-16.7 40.4L229.8 429.1c-22.3 22.3-58.5 22.3-80.8 0L16.7 296.9c-21.7-22.9-20.8-59 2.1-80.7 22.1-21 56.7-20.9 78.7 0l91.9 91.9L414.5 82.9c22.3-22.3 58.5-22.3 80.8 0C506 93.6 512 108.1 512 123.2z"
                />
              </svg>
            </div>
          </div>

          <div className={s.tip}>
            {t('Плохой промпт')}
          </div>
        </div>
      </section>

      {/* Qadamlar */}
      {/* ... остальной контент страницы оставил без изменений ... */}

      {/* Examples */}
      {/* ... остальной контент страницы оставил без изменений ... */}
    



      {/* Qadamlar */}
      <section className={s.qadamlar}>
        <h2 className={`${s.sectionTitle} js-fade ${s.fadeIn}`}>
          {t('Как работает тренировка?')}
        </h2>
        <p className={`${s.sectionSub} js-fade ${s.fadeIn}`}>
          {t('Всё просто')}
        </p>

        <div className={s.qadamlarBlock}>
          <div className={`${s.qadam} js-fade ${s.fadeIn}`}>
            <p className={`${s.qadamNum} ${s.qadamNum1}`}>1</p>
            <div className={s.qadamSvg}>
              {/* Xarita / yo'l xaritasi ikonasi */}
              <svg viewBox="0 0 512 512" width={64} height={64} aria-hidden="true">
                <g>
                  <path d="M323.2 0a16 16 0 0 0-6.4 1.3L192 51.2 67.2 1.3A16 16 0 0 0 48 16v416a16 16 0 0 0 10.8 15.1L192 480l128-48.6 131.2 49.5A16 16 0 0 0 464 480V64a16 16 0 0 0-10.8-15.1L329.6 1.3A16 16 0 0 0 323.2 0zM208 68.6l96-36.4v411.2l-96 36.4zm-32-29.4v410.1l-96-36.3V3zm256 373.5-96 36.3V39.3l96 36.3z" fill="currentColor"/>
                </g>
              </svg>
            </div>
            <h4 className={s.qadamTitle}>{t('step1Title', "Darsni tanlang")}</h4>
            <p className={s.qadamText}>
              {t('step1Text', "Mavzu xaritasiga o'ting, qiziqtirgan darsni bosing va mashqni boshlang.")}
            </p>
          </div>

          <div className={`${s.qadam} js-fade ${s.fadeIn}`}>
            <p className={`${s.qadamNum} ${s.qadamNum2}`}>2</p>
            <div className={s.qadamSvg}>
              {/* Chat / suhbat ikonasi */}
              <svg viewBox="0 0 512 512" width="64" height="64" aria-hidden="true">
                <g>
                  <path d="M256 32C114.6 32 0 125.1 0 240c0 49.6 21.4 95 57 130.7C44.5 421.1 2.7 466 2.2 466.5c-2.2 2.4-2.8 5.9-1.5 8.9S4.8 480 8 480c66.3 0 116-31.8 140.6-51.4C181.7 440.1 217.9 448 256 448c141.4 0 256-93.1 256-208S397.4 32 256 32z" fill="currentColor"/>
                </g>
              </svg>
            </div>
            <h4 className={s.qadamTitle}>{t('step2Title', "AI bilan suhbat")}</h4>
            <p className={s.qadamText}>
              {t('step2Text', "Savol bo'yicha AI bilan erkin suhbatlashing — savollar bering, muhokama qiling, mavzuni o'rganing.")}
            </p>
          </div>

          <div className={`${s.qadam} js-fade ${s.fadeIn}`}>
            <p className={`${s.qadamNum} ${s.qadamNum3}`}>3</p>
            <div className={s.qadamSvg}>
              {/* Tahlil / xulosa ikonasi */}
              <svg viewBox="0 0 512 512" width={64} height={64} aria-hidden="true">
                <g>
                  <path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z" fill="currentColor"/>
                </g>
              </svg>
            </div>
            <h4 className={s.qadamTitle}>{t('step3Title', "Xulosa va tahlil")}</h4>
            <p className={s.qadamText}>
              {t('step3Text', "AI suhbatingizni tahlil qiladi va siz kam muhokama qilgan jihatlarni to'ldiruvchi ma'lumot beradi.")}
            </p>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className={s.examplesBlock}>
        <h2 className={`${s.sectionTitle} js-fade ${s.fadeIn}`}>
          {t('Примеры применения')}
        </h2>
        <p className={`${s.sectionSub} js-fade ${s.fadeIn}`}>
          {t('Где можно использовать')}
        </p>

        <div className={`${s.examples} js-fade ${s.fadeIn}`}>
          <div className={s.exampleCard}>
            <div className={s.icon}>
              <img src="/images/1-1.jpg" alt="" />
            </div>
            <div className={s.exampleCardContent}>
              <h3>{t('Учёба')}</h3>
              <p>{t('Написание эссе,')}</p>
            </div>
          </div>

          <div className={`${s.exampleCard} ${s.exampleCardRight}`}>
            <div className={s.exampleCardContent}>
              <h3>{t('Работа')}</h3>
              <p>{t('Автоматизация задач,')}</p>
            </div>
            <div className={s.icon}>
              <img src="/images/1-2.jpg" alt="" />
            </div>
          </div>

          <div className={s.exampleCard}>
            <div className={s.icon}>
              <img src="/images/1-3.jpg" alt="" />
            </div>
            <div className={s.exampleCardContent}>
              <h3>{t('Креатив')}</h3>
              <p>{t('Генерация идей,')}</p>
            </div>
          </div>

          <div className={`${s.exampleCard} ${s.exampleCardRight}`}>
            <div className={s.exampleCardContent}>
              <h3>{t('Кодинг')}</h3>
              <p>{t('Создание кода,')}</p>
            </div>
            <div className={s.icon}>
              <img src="/images/1-4.jpg" alt="" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
